import '../../database-block/i18n';

import { I18n } from '@affine/i18n';
import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
  ExternalGroupByConfigProvider,
} from '@blocksuite/affine/blocks/database';
import type { ExtensionType } from '@blocksuite/affine/store';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';

import { groupByConfigList } from '../../database-block/group-by';
import { propertiesPresets } from '../../database-block/properties';
import { resolveRelationEndpointAccess } from '../../database-block/properties/relation/access-state';
import {
  canModifyDoc,
  deleteCrossDocInverseRelation,
  isCrossDocRelationServiceInitialized,
  isRelationDeleteInterceptorSuspended,
  runWithRelationDeleteInterceptorSuspended,
} from '../../database-block/properties/relation/database-discovery-bridge';

// =============================================================================
// E012-T016: Property Delete Interceptor for Relation bidirectional cleanup
// =============================================================================

// SVG Icons for dialog (no emojis)
const ICON_LINK = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`;
const ICON_DATABASE = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>`;
const ICON_DOC = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>`;
const ICON_WARNING = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;

function escapeDialogText(value: string): string {
  return value.replace(/[&<>"']/g, char => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return char;
    }
  });
}

/**
 * E012-T016: Show a confirmation dialog for deleting bidirectional relations.
 * Returns the user's choice: 'both' | 'single' | 'cancel'
 */
function showRelationDeleteDialog(
  relationName: string,
  targetDbTitle: string,
  targetDocTitle: string,
  canModifyTarget: boolean
): Promise<'both' | 'single' | 'cancel'> {
  return new Promise(resolve => {
    // IMPORTANT: Use setTimeout to prevent the original click event from
    // propagating to the dialog's Delete button (which would cause immediate deletion)
    setTimeout(() => {
      showDialogContent(
        resolve,
        relationName,
        targetDbTitle,
        targetDocTitle,
        canModifyTarget
      );
    }, 10);
  });
}

function showDialogContent(
  resolve: (value: 'both' | 'single' | 'cancel') => void,
  relationName: string,
  targetDbTitle: string,
  targetDocTitle: string,
  canModifyTarget: boolean
): void {
  // Create overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.65);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      backdrop-filter: blur(4px);
    `;

  // Create dialog
  const dialog = document.createElement('div');
  dialog.style.cssText = `
      background: var(--streamlain-background-overlay-panel-color, #1a1a1a);
      border-radius: 12px;
      border: 1px solid var(--streamlain-border-color, #333);
      box-shadow: 0 24px 48px rgba(0, 0, 0, 0.5);
      width: 420px;
      max-width: 90vw;
      font-family: var(--streamlain-font-family, system-ui);
      color: var(--streamlain-text-primary-color, #e6e6e6);
      overflow: hidden;
    `;

  const noPermissionWarning = !canModifyTarget
    ? `
        <div style="background: rgba(250, 140, 22, 0.1); border: 1px solid rgba(250, 140, 22, 0.25); padding: 12px 14px; border-radius: 8px; margin: 16px 0 0 0; display: flex; align-items: flex-start; gap: 10px;">
          <span style="color: #fa8c16; flex-shrink: 0; margin-top: 1px;">${ICON_WARNING}</span>
          <span style="font-size: 13px; color: #d4a574; line-height: 1.5;">No write access to "${escapeDialogText(targetDocTitle)}". This relation cannot be deleted until both sides can be updated.</span>
        </div>
      `
    : '';

  const options = canModifyTarget
    ? `
        <p style="margin: 20px 0 0 0; font-size: 14px; color: var(--streamlain-text-secondary-color, #a0a0a0);">Both relation columns will be deleted together.</p>
      `
    : '';
  const safeRelationName = escapeDialogText(relationName);
  const safeTargetDbTitle = escapeDialogText(targetDbTitle);
  const safeTargetDocTitle = escapeDialogText(targetDocTitle);

  dialog.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px; padding: 20px 24px; border-bottom: 1px solid var(--streamlain-border-color, #333);">
        <span style="color: var(--streamlain-icon-color, #8e8d91);">${ICON_LINK}</span>
        <span style="font-weight: 600; font-size: 16px; letter-spacing: -0.01em;">Delete Relation</span>
      </div>
      <div style="padding: 20px 24px;">
        <p style="margin: 0 0 16px 0; font-size: 14px; line-height: 1.5; color: var(--streamlain-text-secondary-color, #a0a0a0);">
          The relation "<span style="color: var(--streamlain-text-primary-color, #e6e6e6); font-weight: 500;">${safeRelationName}</span>" has a backlink in:
        </p>
        <div style="background: var(--streamlain-background-secondary-color, #232323); padding: 14px 16px; border-radius: 8px; border: 1px solid var(--streamlain-border-color, #333);">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="color: var(--streamlain-icon-color, #8e8d91);">${ICON_DATABASE}</span>
            <span style="font-weight: 500; font-size: 14px;">${safeTargetDbTitle}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 10px; margin-top: 8px; padding-left: 26px;">
            <span style="color: var(--streamlain-icon-secondary-color, #666);">${ICON_DOC}</span>
            <span style="font-size: 12px; color: var(--streamlain-text-secondary-color, #8e8d91);">${safeTargetDocTitle}</span>
          </div>
        </div>
        ${noPermissionWarning}
        ${options}
      </div>
      <div style="display: flex; justify-content: flex-end; gap: 12px; padding: 16px 24px; border-top: 1px solid var(--streamlain-border-color, #333); background: var(--streamlain-background-secondary-color, #1f1f1f);">
        <button id="relationDialogCancelBtn" style="padding: 10px 20px; border-radius: 8px; border: 1px solid var(--streamlain-border-color, #444); background: transparent; color: var(--streamlain-text-primary-color, #e6e6e6); cursor: pointer; font-size: 14px; font-weight: 500; transition: all 0.15s ease;">
          Cancel
        </button>
        <button id="relationDialogDeleteBtn" style="padding: 10px 20px; border-radius: 8px; border: none; background: var(--streamlain-error-color, #dc3545); color: white; cursor: pointer; font-size: 14px; font-weight: 500; transition: all 0.15s ease;">
          ${canModifyTarget ? 'Delete' : 'OK'}
        </button>
      </div>
    `;

  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  // Add hover effects
  // NOTE: Using unique IDs to avoid conflicts with Blocksuite's deleteBtn handlers
  const cancelBtn = dialog.querySelector(
    '#relationDialogCancelBtn'
  ) as HTMLButtonElement;
  const deleteBtn = dialog.querySelector(
    '#relationDialogDeleteBtn'
  ) as HTMLButtonElement;
  const optSingle = dialog.querySelector('#optSingle') as HTMLLabelElement;
  const optBoth = dialog.querySelector('#optBoth') as HTMLLabelElement;

  if (cancelBtn) {
    cancelBtn.onmouseenter = () => {
      cancelBtn.style.background = 'var(--streamlain-hover-color, #2a2a2a)';
    };
    cancelBtn.onmouseleave = () => {
      cancelBtn.style.background = 'transparent';
    };
  }

  if (deleteBtn) {
    deleteBtn.onmouseenter = () => {
      deleteBtn.style.opacity = '0.9';
    };
    deleteBtn.onmouseleave = () => {
      deleteBtn.style.opacity = '1';
    };
  }

  // Update option styling on selection
  const updateOptionStyles = () => {
    const singleInput = dialog.querySelector(
      'input[value="single"]'
    ) as HTMLInputElement;
    const singleCircle = optSingle?.querySelector(
      '.radio-circle'
    ) as HTMLElement;
    const singleDot = optSingle?.querySelector('.radio-dot') as HTMLElement;
    const bothCircle = optBoth?.querySelector('.radio-circle') as HTMLElement;
    const bothDot = optBoth?.querySelector('.radio-dot') as HTMLElement;

    if (optSingle && optBoth) {
      if (singleInput?.checked) {
        // Single selected
        optSingle.style.background = 'rgba(30, 150, 235, 0.08)';
        optSingle.style.borderColor =
          'var(--streamlain-primary-color, #1e96eb)';
        if (singleCircle)
          singleCircle.style.borderColor =
            'var(--streamlain-primary-color, #1e96eb)';
        if (singleDot) {
          singleDot.style.width = '10px';
          singleDot.style.height = '10px';
        }

        optBoth.style.background =
          'var(--streamlain-background-secondary-color, #232323)';
        optBoth.style.borderColor = 'var(--streamlain-border-color, #333)';
        if (bothCircle)
          bothCircle.style.borderColor = 'var(--streamlain-border-color, #555)';
        if (bothDot) {
          bothDot.style.width = '0';
          bothDot.style.height = '0';
        }
      } else {
        // Both selected
        optBoth.style.background = 'rgba(30, 150, 235, 0.08)';
        optBoth.style.borderColor = 'var(--streamlain-primary-color, #1e96eb)';
        if (bothCircle)
          bothCircle.style.borderColor =
            'var(--streamlain-primary-color, #1e96eb)';
        if (bothDot) {
          bothDot.style.width = '10px';
          bothDot.style.height = '10px';
        }

        optSingle.style.background =
          'var(--streamlain-background-secondary-color, #232323)';
        optSingle.style.borderColor = 'var(--streamlain-border-color, #333)';
        if (singleCircle)
          singleCircle.style.borderColor =
            'var(--streamlain-border-color, #555)';
        if (singleDot) {
          singleDot.style.width = '0';
          singleDot.style.height = '0';
        }
      }
    }
  };

  // Add click handlers to labels for radio selection
  optSingle?.addEventListener('click', () => {
    const input = optSingle.querySelector('input') as HTMLInputElement;
    if (input) {
      input.checked = true;
      updateOptionStyles();
    }
  });
  optBoth?.addEventListener('click', () => {
    const input = optBoth.querySelector('input') as HTMLInputElement;
    if (input) {
      input.checked = true;
      updateOptionStyles();
    }
  });

  // Handle cancel
  const cleanup = () => {
    document.body.removeChild(overlay);
  };

  overlay.addEventListener('click', e => {
    if (e.target === overlay) {
      e.stopPropagation();
      e.preventDefault();
      cleanup();
      resolve('cancel');
    }
  });

  cancelBtn?.addEventListener('click', e => {
    e.stopPropagation();
    e.preventDefault();
    cleanup();
    resolve('cancel');
  });

  deleteBtn?.addEventListener('click', e => {
    // CRITICAL: Stop event propagation to prevent click from reaching
    // underlying elements after dialog is removed
    e.stopPropagation();
    e.preventDefault();

    if (!canModifyTarget) {
      cleanup();
      resolve('cancel');
      return;
    }

    const selected = dialog.querySelector<HTMLInputElement>(
      'input[name="deleteOption"]:checked'
    );
    cleanup();
    resolve((selected?.value as 'both' | 'single') || 'both');
  });

  // Escape key handler
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      cleanup();
      resolve('cancel');
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}

function showRelationDeleteBlocked(message: string): Promise<void> {
  const modal = window.__confirmModal;
  if (!modal) {
    window.alert(message);
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    modal.openConfirmModal({
      title: 'Relation could not be deleted',
      description: message,
      confirmText: 'OK',
      cancelText: 'Cancel',
      confirmButtonOptions: { variant: 'error' },
      onConfirm: () => resolve(),
      onCancel: () => resolve(),
    });
  });
}

/**
 * Confirm removing a relation column whose counterpart is gone (never wired,
 * target database trashed or deleted, inverse column already removed).
 * Resolves `true` when the user confirms. Fails closed without the modal.
 */
function showRelationOrphanDeleteConfirm(
  propertyName: string
): Promise<boolean> {
  const modal = window.__confirmModal;
  if (!modal) {
    console.error(
      '[PropertyDelete] Confirm modal unavailable; refusing orphan relation delete'
    );
    return Promise.resolve(false);
  }
  return new Promise<boolean>(resolve => {
    let settled = false;
    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    modal.openConfirmModal({
      title: I18n.t('com.streamlain.streamlain.relation-delete.orphan-title'),
      description: I18n.t(
        'com.streamlain.streamlain.relation-delete.orphan-message',
        { name: propertyName }
      ),
      confirmText: I18n.t(
        'com.streamlain.streamlain.relation-delete.orphan-confirm'
      ),
      cancelText: I18n.t('Cancel'),
      confirmButtonOptions: {
        variant: 'error',
        ...({ 'data-testid': 'relation-orphan-delete-confirm' } as Record<
          string,
          string
        >),
      },
      onConfirm: () => settle(true),
      onCancel: () => settle(false),
      onOpenChange: open => {
        if (!open) settle(false);
      },
    });
  });
}

/**
 * E012-T016: Patch DatabaseBlockDataSource.propertyDelete to intercept
 * relation property deletions and show a confirmation dialog.
 *
 * Decision tree for relation columns:
 * - no `targetDatabaseId`: never wired, delete silently.
 * - counterpart alive (`inverseFieldId` set, target discovered and not
 *   trashed): two-sides / single / cancel dialog; the inverse side is removed
 *   first and any failure there aborts the whole delete.
 * - counterpart gone (no `inverseFieldId`, target missing or trashed, or the
 *   target is discoverable only as restricted but the user may edit its doc):
 *   orphan confirm, then remove the source column. A still-present inverse
 *   column is removed as well when the target is reachable.
 * - target restricted and not editable: blocked.
 */
let isPatched = false;

function patchPropertyDelete(): void {
  if (isPatched) return;
  isPatched = true;

  const originalPropertyDelete =
    DatabaseBlockDataSource.prototype.propertyDelete;

  const deleteSameDocInverse = (
    store: DatabaseBlockDataSource['doc'],
    targetDatabaseId: string,
    inverseFieldId: string
  ): boolean => {
    const databases = store.getBlocksByFlavour('affine:database');
    const targetDb = databases.find(
      (db: { id: string }) => db.id === targetDatabaseId
    );
    if (!targetDb) {
      console.warn(
        '[PropertyDelete] Same-doc relation target is gone; removing source column only:',
        targetDatabaseId
      );
      return true;
    }
    const targetDataSource = createDatabaseBlockDataSource(
      targetDb.model as DatabaseBlockModel
    );
    const properties =
      targetDataSource.getBoundedPropertyIdsWindow().propertyIds;
    if (!properties.includes(inverseFieldId)) {
      console.warn(
        '[PropertyDelete] Inverse relation column already gone; removing source column only:',
        inverseFieldId
      );
      return true;
    }
    originalPropertyDelete.call(targetDataSource, inverseFieldId);
    console.log('[PropertyDelete] Deleted inverse relation:', inverseFieldId);
    return true;
  };

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  DatabaseBlockDataSource.prototype.propertyDelete = async function (
    propertyId: string
  ): Promise<void> {
    // Confirmed flows remove counterpart columns programmatically; they must
    // not trigger nested dialogs.
    if (isRelationDeleteInterceptorSuspended()) {
      return originalPropertyDelete.call(this, propertyId);
    }

    const propertyType = this.propertyTypeGet(propertyId);
    if (propertyType !== 'relation') {
      return originalPropertyDelete.call(this, propertyId);
    }

    const propertyData = this.propertyDataGet(propertyId);
    const targetDatabaseId =
      typeof propertyData?.targetDatabaseId === 'string'
        ? propertyData.targetDatabaseId
        : '';
    if (!targetDatabaseId) {
      console.log('[PropertyDelete] Unwired relation, deleting silently');
      return originalPropertyDelete.call(this, propertyId);
    }

    const store = this.doc;
    const currentDocId = store.id || '';
    const inverseFieldId =
      typeof propertyData?.inverseFieldId === 'string'
        ? propertyData.inverseFieldId
        : '';
    const propertyName = this.propertyNameGet(propertyId) || 'Relation';
    const targetAccess = resolveRelationEndpointAccess({
      targetDatabaseId,
      targetDocId:
        typeof propertyData?.targetDocId === 'string'
          ? propertyData.targetDocId
          : undefined,
    });
    const targetDocId = targetAccess.targetDocId || currentDocId;
    const isCrossDoc = targetDocId !== currentDocId;

    // Check permission for cross-doc. Without the bridge we cannot update the
    // inverse side, so deletion must stay blocked.
    let canModifyTarget = !isCrossDoc;
    if (isCrossDoc && isCrossDocRelationServiceInitialized()) {
      try {
        canModifyTarget = await canModifyDoc(targetDocId);
      } catch {
        canModifyTarget = false;
      }
    }

    const counterpartAlive =
      inverseFieldId !== '' &&
      (targetAccess.state === 'write' || targetAccess.state === 'read') &&
      targetAccess.database?.columns.some(
        column => column.id === inverseFieldId
      ) === true;

    let removeInverse: boolean;
    if (counterpartAlive) {
      let targetDbTitle = targetAccess.databaseTitle;
      let targetDocTitle = targetAccess.docTitle;
      if (!targetDbTitle) {
        targetDbTitle = `Database ${targetDatabaseId.slice(0, 8)}...`;
      }
      if (!targetDocTitle) {
        targetDocTitle = `Document ${targetDocId.slice(0, 8)}...`;
      }
      const choice = await showRelationDeleteDialog(
        propertyName,
        targetDbTitle,
        targetDocTitle,
        canModifyTarget
      );
      if (choice === 'cancel') {
        return;
      }
      removeInverse = choice === 'both';
    } else if (
      inverseFieldId !== '' &&
      targetAccess.state === 'restricted' &&
      !canModifyTarget
    ) {
      await showRelationDeleteBlocked(
        'This relation cannot be deleted because its target database is not accessible.'
      );
      return;
    } else {
      const confirmed = await showRelationOrphanDeleteConfirm(propertyName);
      if (!confirmed) {
        return;
      }
      // A writable but unopened target is unresolved, not an orphan. Keep
      // the existing authoritative load-and-delete path for that case.
      removeInverse =
        targetAccess.state === 'restricted' && inverseFieldId !== '';
    }

    if (removeInverse) {
      const inverseDeleted = await runWithRelationDeleteInterceptorSuspended(
        async (): Promise<boolean> => {
          if (isCrossDoc) {
            if (!isCrossDocRelationServiceInitialized()) {
              await showRelationDeleteBlocked(
                'This relation cannot be deleted because the cross-doc relation service is not available.'
              );
              return false;
            }
            const result = await deleteCrossDocInverseRelation(
              targetDocId,
              targetDatabaseId,
              inverseFieldId
            );
            if (!result.success) {
              console.error(
                '[PropertyDelete] Failed to delete cross-doc inverse:',
                result.error
              );
              await showRelationDeleteBlocked(
                result.error?.message ??
                  'The inverse relation could not be deleted.'
              );
              return false;
            }
            return true;
          }
          try {
            return deleteSameDocInverse(
              store,
              targetDatabaseId,
              inverseFieldId
            );
          } catch (e) {
            console.error(
              '[PropertyDelete] Failed to delete local inverse:',
              e
            );
            await showRelationDeleteBlocked(
              e instanceof Error
                ? e.message
                : 'The inverse relation could not be deleted.'
            );
            return false;
          }
        }
      );
      if (!inverseDeleted) {
        return;
      }
    }

    // Now delete the original property
    const result = originalPropertyDelete.call(this, propertyId);

    // Dispatch event to trigger ghost suppression and rescan after relation deletion
    // EdgeIntegrationService listens for this event
    // Include targetDatabaseId so ghost suppression can be applied
    window.dispatchEvent(
      new CustomEvent('streamlain:relation-deleted', {
        detail: { targetDatabaseId },
      })
    );

    return result;
  };
}

export function patchDatabaseBlockConfigService(): ExtensionType[] {
  //TODO use service
  DatabaseBlockDataSource.externalProperties.value = propertiesPresets;

  // E012-T016: Install property delete interceptor
  patchPropertyDelete();

  // Note: Database Fullscreen button is now rendered directly in the database header
  // (see database-block.ts renderFullscreenButton) instead of as a menu item

  return [
    {
      setup: di => {
        groupByConfigList.forEach(config => {
          di.addValue(ExternalGroupByConfigProvider(config.name), config);
        });
      },
    },
  ];
}
